/**
 * The crew iPad's visit card — `docs/visit-page.md` §13.4, Board 9 (*What
 * Marco sees*), and the crew app's own card once PR 5 mounts it there (§20.5:
 * *the visit card shows it under Before you start*).
 *
 * Exactly the card, drawn from `CrewIpadCard` — the crew app's own read, made
 * as the crew lead (§13.4: "never a separate drawing"): the visit mark at 44,
 * the place, *Day 2 of 3 · <the visit>*, the address and **Navigate**,
 * *BEFORE YOU START · 4* with each note and its chip, and **Plans · 3 · Map ·
 * 2 · Load list**. No money, no prices, no internal notes — the read carries
 * none.
 */
import type { CrewIpadCard } from '@rowted/shared'
import { cn } from '../lib/cn.js'
import { VisitGlyph, type VisitGlyphName } from './glyphs.js'
import { SourceChip } from './crew-parts.js'
import { VisitMark } from './visit-mark.js'
import { CREW_PREVIEW } from './copy.js'

function CardButton({ glyph, children, onClick }: { glyph: VisitGlyphName; children: string; onClick?: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn('inline-flex h-[38px] flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-[10px]',
        'border border-line bg-surface px-3.5 text-[12.5px] font-semibold text-text hover:bg-hover')}
    >
      <VisitGlyph name={glyph} size={14.5} stroke={2} />
      {children}
    </button>
  )
}

export function CrewIpadCardView({
  card, onPlans, onMap, onLoadList, className,
}: {
  card: CrewIpadCard
  onPlans?: () => void
  onMap?: () => void
  onLoadList?: () => void
  className?: string
}) {
  const address = card.address ? `${card.address.line1}, ${card.address.city}` : null
  return (
    <div
      className={cn('w-full max-w-[416px] overflow-hidden rounded-[24px] border-8 border-device-bezel bg-bg shadow-popover',
        className)}
    >
      <div className="flex flex-col gap-1.5 border-b border-line bg-surface px-4 pb-3 pt-3.5">
        <div className="flex items-center gap-2.5">
          <VisitMark mark={card.mark} size={44} />
          <div className="flex min-w-0 flex-col">
            <b className="truncate text-[15px] font-bold text-text">{card.title}</b>
            <span className="truncate text-[12px] text-text-muted">
              {card.day
                ? CREW_PREVIEW.sub(String(card.day.index), String(card.day.count), card.visitName)
                : card.visitName}
            </span>
          </div>
        </div>
        {address && (
          <div className="flex items-center gap-2">
            <span className="min-w-0 truncate text-[12.5px] text-text-body">{address}</span>
            <span className="flex-1" />
            <a
              href={`https://maps.google.com/?q=${encodeURIComponent(address)}`}
              target="_blank"
              rel="noopener noreferrer"
              className={cn('inline-flex h-8 shrink-0 items-center justify-center gap-1.5 rounded-control border',
                'border-accent bg-accent px-3.5 text-[12.5px] font-semibold text-white hover:bg-accent-dark')}
            >
              <VisitGlyph name="navigate" size={14.5} stroke={2} />
              {CREW_PREVIEW.navigate}
            </a>
          </div>
        )}
      </div>

      {card.notes.length > 0 && (
        <div className="bg-surface px-4 py-2.5">
          <div className="text-[10.5px] font-semibold uppercase tracking-[.06em] text-text-faint">
            {CREW_PREVIEW.beforeYouStart(String(card.notes.length))}
          </div>
          {card.notes.map((note, index) => (
            // A note's words and its home are the whole row; two notes may say the same thing.
            <div key={index} className="flex items-start gap-2 border-t border-line-faint py-[9px]">
              <SourceChip source={note.source} />
              <span className="text-[12.5px] leading-[1.45] text-text">{note.text}</span>
            </div>
          ))}
        </div>
      )}

      <div className="flex gap-2 border-t border-line bg-surface px-4 pb-3.5 pt-2.5">
        <CardButton glyph="doc" onClick={onPlans}>{CREW_PREVIEW.plans(String(card.plans))}</CardButton>
        <CardButton glyph="map" onClick={onMap}>{CREW_PREVIEW.map(String(card.maps))}</CardButton>
        <CardButton glyph="truck" onClick={onLoadList}>{CREW_PREVIEW.loadList}</CardButton>
      </div>
    </div>
  )
}
